import * as fabric from "fabric";
import { resSize } from "./resSize";
import type { RecordingQuality } from "../context/RecorderContext";

/**
 * Upgrades Fabric canvas backing store to match recording quality
 * while maintaining proper CSS display dimensions
 */
export const upgradeFabricBackstoreToQuality = (
  fabricCanvas: fabric.Canvas,
  quality: RecordingQuality,
  canvasRatio: string,
  canvasWidth: number,
  canvasHeight: number
) => {
  // A) target pixels from resSize helper (backing store resolution)
  const { width: targetW, height: targetH } = resSize(quality, canvasRatio);
  
  // Target aspect ratio from the requested canvas ratio
  const targetAR = targetW / targetH;

  // B) CSS display size - calculate based on target aspect ratio
  // Get the wrapper parent to determine available space
  const wrapperEl =
    (fabricCanvas as any).wrapperEl ||
    fabricCanvas.upperCanvasEl?.parentElement ||
    fabricCanvas.lowerCanvasEl?.parentElement;
  
  const parentEl = wrapperEl?.parentElement;
  const availableWidth = parentEl?.clientWidth || 1046;
  const availableHeight = parentEl?.clientHeight || 588;
  
  // Calculate CSS dimensions that fit within container while maintaining target aspect ratio
  let cssW: number, cssH: number;
  const containerAR = availableWidth / availableHeight;
  
  if (targetAR > containerAR) {
    // Target is wider than container - fit to width
    cssW = availableWidth;
    cssH = Math.round(availableWidth / targetAR);
  } else {
    // Target is taller than container - fit to height
    cssH = availableHeight;
    cssW = Math.round(availableHeight * targetAR);
  }

  // 1) set backstore (attrs) to target pixels
  fabricCanvas?.setDimensions({ width: targetW, height: targetH });

  // 2) set CSS size to wrapper size (no rounding drift)
  fabricCanvas?.setDimensions(
    { width: cssW, height: cssH },
    { cssOnly: true }
  );

  // 3) ensure wrapper matches your layout (wrapperEl already defined above)
  if (wrapperEl) {
    wrapperEl.style.width = `${cssW}px`;
    wrapperEl.style.height = `${cssH}px`;
  }

  // 4) Keep viewportTransform at identity - the backing store IS the coordinate system
  // Objects are positioned in backing store coordinates and CSS scales them visually
  fabricCanvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

  // keep DOM canvas styles in sync - CSS scales the high-res backing store down
  fabricCanvas.upperCanvasEl.style.width = `100%`;
  fabricCanvas.upperCanvasEl.style.height = `100%`;
  fabricCanvas.lowerCanvasEl.style.width = `100%`;
  fabricCanvas.lowerCanvasEl.style.height = `100%`;

  // 5) Recalculate canvas offset for accurate mouse coordinate detection
  if (typeof (fabricCanvas as any).calcOffset === "function") {
    (fabricCanvas as any).calcOffset();
  }

  // 6) Update all object coordinates to match the new zoom level
  fabricCanvas.forEachObject((obj) => {
    obj.setCoords();
  });

  fabricCanvas.renderAll();
};

/**
 * Get the scale factor between CSS display size and backing store size
 * This is useful for converting CSS coordinates to Fabric coordinates
 */
export const getBackingStoreScale = (
  fabricCanvas: fabric.Canvas,
  cssWidth: number,
  cssHeight: number
): number => {
  const backingWidth = fabricCanvas.getWidth();
  const backingHeight = fabricCanvas.getHeight();
  // Scale factor: how many backing store pixels per CSS pixel
  return backingWidth / cssWidth; // or backingHeight / cssHeight (same ratio)
};
